import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import type { ObbTakeoffData } from '../data/obbTakeoff';

// Matches MultiSceneViewer sceneQuaternion — raw Z-up → Three.js Y-up.
const SPLAT_QUATERNION = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));

// OBB edge connectivity (indices into the 8-point array):
// P0-P3 bottom face, P4-P7 top face, verticals Pi↔P(i+4)
const EDGE_PAIRS = [
  [0,1],[1,3],[3,2],[2,0],  // bottom face
  [4,5],[5,7],[7,6],[6,4],  // top face
  [0,4],[1,5],[2,6],[3,7],  // verticals
] as const;

function buildObbGroup(obbRaw: [number, number, number][], color: string): THREE.Group {
  const hex = parseInt(color.replace('#', ''), 16);

  const positions: number[] = [];
  for (const [a, b] of EDGE_PAIRS) {
    const pa = obbRaw[a], pb = obbRaw[b];
    positions.push(pa[0], pa[1], pa[2], pb[0], pb[1], pb[2]);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  const mat = new THREE.LineBasicMaterial({
    color: hex, linewidth: 1, depthTest: false, transparent: true, opacity: 0.9,
  });
  const lines = new THREE.LineSegments(geo, mat);
  lines.renderOrder = 10;

  // Top face fill
  const tp = [4, 5, 7, 6].map(i => obbRaw[i]);
  const fillGeo = new THREE.BufferGeometry();
  fillGeo.setAttribute('position', new THREE.Float32BufferAttribute([
    ...tp[0], ...tp[1], ...tp[2],
    ...tp[0], ...tp[2], ...tp[3],
  ], 3));
  const fillMat = new THREE.MeshBasicMaterial({
    color: hex, transparent: true, opacity: 0.15,
    side: THREE.DoubleSide, depthTest: false,
  });
  const fill = new THREE.Mesh(fillGeo, fillMat);
  fill.renderOrder = 9;

  const group = new THREE.Group();
  group.quaternion.copy(SPLAT_QUATERNION);
  group.add(lines, fill);
  return group;
}

interface Props {
  viewerRef: React.MutableRefObject<{
    threeScene?: THREE.Scene;
    camera?: THREE.PerspectiveCamera;
    renderer?: THREE.WebGLRenderer;
  }>;
  data: ObbTakeoffData;
}

export function ObbTakeoffOverlay({ viewerRef, data }: Props) {
  const rafRef = useRef<number | null>(null);
  const labelRefs = useRef<{ a: HTMLDivElement | null; b: HTMLDivElement | null; c: HTMLDivElement | null }>(
    { a: null, b: null, c: null }
  );

  // raw Z-up → Three.js world Y-up
  const rawToWorld = (raw: [number, number, number]): THREE.Vector3 =>
    new THREE.Vector3(raw[0], raw[2], -raw[1]);

  useEffect(() => {
    const scene = viewerRef.current.threeScene;
    if (!scene) return;

    const group = buildObbGroup(data.obbPoints, data.color);
    scene.add(group);

    const anchors = {
      a: rawToWorld(data.dimensionAnchors.a.point),
      b: rawToWorld(data.dimensionAnchors.b.point),
      c: rawToWorld(data.dimensionAnchors.c.point),
    };
    const proj = new THREE.Vector3();

    const tick = () => {
      rafRef.current = requestAnimationFrame(tick);
      const camera = viewerRef.current.camera;
      const canvas = viewerRef.current.renderer?.domElement;
      if (!camera || !canvas) return;
      const rect = canvas.getBoundingClientRect();
      for (const key of ['a', 'b', 'c'] as const) {
        const el = labelRefs.current[key];
        if (!el) continue;
        proj.copy(anchors[key]).project(camera);
        if (proj.z > 1) { el.style.display = 'none'; continue; }
        const x = (proj.x + 1) / 2 * rect.width  + rect.left;
        const y = (1 - proj.y) / 2 * rect.height + rect.top;
        el.style.display = 'block';
        el.style.transform = `translate(-50%,-50%) translate(${x}px,${y}px)`;
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      scene.remove(group);
      group.traverse(child => {
        if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) {
          child.geometry.dispose();
          (child.material as THREE.Material).dispose();
        }
      });
    };
  }, [data, viewerRef]);

  const pillBase: React.CSSProperties = {
    position: 'fixed', top: 0, left: 0,
    pointerEvents: 'none', zIndex: 1020,
    background: 'rgba(0,0,0,0.75)',
    border: `1px solid ${data.color}99`,
    color: data.color,
    borderRadius: '6px', padding: '3px 8px',
    fontSize: '12px', fontWeight: 600, letterSpacing: '0.03em',
    whiteSpace: 'nowrap', display: 'none',
  };

  return (
    <>
      <div ref={el => { labelRefs.current.a = el; }} style={pillBase}>{data.dimensionAnchors.a.label}</div>
      <div ref={el => { labelRefs.current.b = el; }} style={pillBase}>{data.dimensionAnchors.b.label}</div>
      <div ref={el => { labelRefs.current.c = el; }} style={pillBase}>{data.dimensionAnchors.c.label}</div>
    </>
  );
}

export default ObbTakeoffOverlay;
